import { Link, createFileRoute, useParams } from '@tanstack/react-router';

import { useRfmSummary } from '@/features/rfm';
import { useSyncStatus } from '@/features/sync';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { AnimatedContainer } from '@/shared/ui/animated';
import { MemberOnlyNotice } from '@/shared/ui/member-only-notice';
import { NeedsSyncState } from '@/shared/ui/needs-sync-state';
import { PageHeader } from '@/shared/ui/page-header';

import { AppLayout } from '../../-components/app-layout';
import { CustomerTable } from '../../-components/customer-table';
import { SegmentSummaryCards } from '../../-components/segment-summary-cards';

export const Route = createFileRoute('/_authenticated/$mallId/analysis/rfm/')({
  component: RfmPage,
});

function RfmPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/analysis/rfm/' });
  const { data: syncStatus, isLoading: isSyncLoading } = useSyncStatus(mallId);
  const { data: summary, isLoading } = useRfmSummary();

  const needsSync = !isSyncLoading && !syncStatus?.lastSyncAt;

  if (needsSync && !isLoading) {
    return (
      <AppLayout pageTitle="고객 상태">
        <NeedsSyncState />
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="고객 상태">
      <div className="space-y-6 max-w-6xl mx-auto">
        <AnimatedContainer delay={0}>
          <PageHeader
            title="고객 상태"
            description="최근 구매·구매 빈도·구매 금액을 기준으로 고객을 분류합니다"
          />
        </AnimatedContainer>
        <AnimatedContainer delay={0.05}>
          <MemberOnlyNotice />
        </AnimatedContainer>
        <AnimatedContainer delay={0.1}>
          <AnalysisGuide title="이 분석은 이렇게 읽어요">
            <p className="text-xs text-muted-foreground/70">
              이 분석의 다른 이름은 <strong>RFM 분석</strong>이에요.
            </p>
            <p>
              고객을 세 가지 기준으로 나눠요. <strong>R</strong> 최근에 샀는지, <strong>F</strong>{' '}
              자주 사는지, <strong>M</strong> 많이 쓰는지.
            </p>
            <p>세 가지를 종합해서 고객을 신호등처럼 분류합니다.</p>
            <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
              <span className="bg-signal-green-soft text-signal-green-text rounded-lg px-2.5 py-1.5">
                활성: 최근 30일 이내 · 5회 이상 · 10만원 이상
              </span>
              <span className="bg-signal-yellow-soft text-signal-yellow-text rounded-lg px-2.5 py-1.5">
                주의: 최근 60일 이내 · 2회 이상 · 3만원 이상
              </span>
              <span className="bg-signal-red-soft text-signal-red-text rounded-lg px-2.5 py-1.5">
                이탈: 위 조건에 해당하지 않는 고객
              </span>
            </div>
            <p className="mt-2">
              이 기준은 우리 쇼핑몰에 맞게{' '}
              <Link
                to="/$mallId/settings"
                params={{ mallId }}
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
              >
                설정 페이지
              </Link>
              에서 바꿀 수 있어요.
            </p>
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="font-medium text-foreground/80">이렇게 활용해 보세요</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                <li>
                  <strong>활성 고객</strong>에게는 VIP 혜택이나 신상품 우선 안내로 충성도를
                  높여보세요.
                </li>
                <li>
                  <strong>주의 고객</strong>은 이탈 직전이에요. 한정 쿠폰이나 "다시 만나서 반가워요"
                  메시지가 효과적이에요.
                </li>
                <li>
                  <strong>이탈 고객</strong>은 대폭 할인이나 무료 배송 같은 강한 인센티브로 재방문을
                  유도해 보세요.
                </li>
              </ul>
            </div>
          </AnalysisGuide>
        </AnimatedContainer>
        <AnimatedContainer delay={0.2}>
          <SegmentSummaryCards data={summary} isLoading={isLoading} />
        </AnimatedContainer>
        <AnimatedContainer delay={0.3}>
          <CustomerTable />
        </AnimatedContainer>
      </div>
    </AppLayout>
  );
}
